import { create } from 'zustand';
import { message } from 'antd';
import { axiosApi } from '../../../axios-api';
import { handleGenerationsLimitError } from '../../../helpers';

const useDesignStore = create((set, get) => ({
  api: null,
  projectId: null,
  designId: null,

  design: undefined,
  designName: null,
  userImages: [],
  ruleId: undefined,

  init: (api, projectId, designId) => {
    set({ api, projectId, designId });
  },

  setDesign: (design) => set({ design }),
  setDesignName: (designName) => set({ designName }),
  setUserImages: (userImages) => set({ userImages }),
  setRuleId: (ruleId) => set({ ruleId }),

  loadDesign: async () => {
    const { api, projectId, designId } = get();
    if (!designId) return;
    try {
      const data = await api.fetchDesign(projectId, designId);
      set({ design: data });
      return data;
    } catch (error) {
      message.error(error?.message);
    }
  },

  saveDesign: async (stageRef, canvasSize) => {
    const { designName, designId, projectId, api, userImages } = get();
    const stage = stageRef;
    if (!stage) return;

    try {
      const stageJSON = stage.toJSON();

      const customFontsImages = [];
      for (const child of stage.findOne('#canvas')?.children ?? []) {
        if (!child.hasName('elementGroup')) continue;
        const main = child.findOne('.main');
        if (!main) continue;
        const fontFamily = main.getAttr('fontFamily');
        const url = main.getAttr('url');
        if (fontFamily) customFontsImages.push(fontFamily);
        if (url) customFontsImages.push(url);
      }

      const payload = {
        name: designName,
        canvas_width: canvasSize.width,
        canvas_height: canvasSize.height,
        design_json: stageJSON,
      };

      if (designId) {
        await axiosApi.put(`/projects/${projectId}/designs/${designId}/`, payload);
      } else {
        const { data } = await axiosApi.post(`/projects/${projectId}/designs/`, payload);
        set({ designId: data.id });
      }

      message.success('Design saved!');
    } catch (error) {
      if (error.isAxiosError) {
        handleGenerationsLimitError(error);
      } else {
        message.error(error?.message);
      }
    }
  },

  getImages: async () => {
    const { api } = get();
    try {
      const data = await api.getImages?.();
      if (data) set({ userImages: data });
      return data;
    } catch (error) {
      message.error(error?.message);
    }
  },

  uploadImage: async (file) => {
    const { api, userImages } = get();
    try {
      const data = await api.uploadImage?.(file);
      if (data) set({ userImages: [...userImages, data] });
      return data;
    } catch (error) {
      message.error(error?.message);
    }
  },

  deleteImage: async (imageId) => {
    const { api, userImages } = get();
    try {
      await api.deleteImage?.(imageId);
      set({ userImages: userImages.filter((img) => img.id !== imageId) });
    } catch (error) {
      message.error(error?.message);
    }
  },

  reset: () =>
    set({
      design: undefined,
      designName: null,
      userImages: [],
      ruleId: undefined,
    }),
}));

export default useDesignStore;
